import $ from 'jquery';
import { fetchAiCredits } from '~graphql/controllers';
import { fetchCreditModules } from '~src/~core/payments';
import { handleBuyAiCredits } from './handleBuyAiCredits';

/**
 * Black Friday promo end date configuration
 */
export const BF_PROMO_END_DATE = new Date('2025-12-05T23:59:59');

/**
 * DEBUG: Set to true to force showing the countdown timer regardless of time remaining
 * Set to false for production
 */
export const BF_DEBUG_FORCE_COUNTDOWN = false;

/**
 * Check if the Black Friday promotion is currently active
 * Promotion runs until December 5, 2025 (end of day)
 */
export const isBlackFridayActive = () => {
  const now = new Date();
  return now <= BF_PROMO_END_DATE;
};

/**
 * Get time remaining until promo ends
 * Returns null if promo has ended, otherwise returns object with days, hours, minutes, seconds
 */
export const getTimeRemaining = () => {
  const now = new Date();
  const diff = BF_PROMO_END_DATE - now;

  if (diff <= 0) return null;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds, totalMs: diff };
};

/**
 * Check if less than 24 hours remain in the promo
 * When BF_DEBUG_FORCE_COUNTDOWN is true, always returns true (for testing)
 */
export const isLast24Hours = () => {
  if (BF_DEBUG_FORCE_COUNTDOWN) return true;
  const remaining = getTimeRemaining();
  return remaining && remaining.hours < 24;
};

export const initAiBlackFridayModal = () => {
  // Remove existing listeners to prevent duplicates
  $(document).off('click.aiBlackFriday', '#bf-credits-info-btn');
  $('#aiBlackFridayModal').off('show.bs.modal.aiBlackFriday');
  $('#aiBlackFridayModal').off('hidden.bs.modal.aiBlackFriday');
  $('#aiBlackFridayModal').off('shown.bs.modal.aiBlackFriday');

  // Handle credits info button click
  $(document).on('click.aiBlackFriday', '#bf-credits-info-btn', function (e) {
    e.preventDefault();
    $('#aiBlackFridayModal').modal('hide');

    // Wait for modal to be fully hidden before showing the next one
    setTimeout(function () {
      $('#aiCreditsInfoModal').modal('show');
      // Ensure body has modal-open class and backdrop is correct
      $('body').addClass('modal-open');
    }, 500);
  });

  // Prevent ESC key from propagating to global handlers when modal is shown
  $('#aiBlackFridayModal').on('show.bs.modal.aiBlackFriday', function () {
    $(this).on('keydown.aiModal', function (e) {
      if (e.keyCode === 27) {
        e.stopPropagation();
      }
    });
  });

  // Clean up event handler when modal is hidden
  $('#aiBlackFridayModal').on('hidden.bs.modal.aiBlackFriday', function () {
    $(this).off('keydown.aiModal');
  });

  // Load packages when modal opens
  $('#aiBlackFridayModal').on(
    'shown.bs.modal.aiBlackFriday',
    async function () {
      const container = $('#blackFridayPackagesContainer');

      // Show loading state
      container.html(`
      <div class="col-12 loading-placeholder">
        <div class="spinner-border text-warning" role="status">
          <span class="sr-only">Caricamento...</span>
        </div>
        <p class="mt-3">Caricamento offerte Black Friday...</p>
      </div>
    `);

      try {
        // Fetch current credits
        const credits = await fetchAiCredits();
        const totalCredits =
          credits.edges[0].freeCreditsAmount +
          credits.edges[0].paidCreditsAmount;
        $('#bfCurrentCreditsBalance').text(totalCredits);

        // Fetch available Black Friday packages
        const modules = await fetchCreditModules();

        if (!modules || modules.length === 0) {
          container.html(`
          <div class="col-12 text-center">
            <p class="text-muted">Nessun pacchetto disponibile al momento.</p>
          </div>
        `);
          return;
        }

        // Black Friday discount configuration (hardcoded)
        const bfDiscounts = {
          'ADD-CRE250-ST-25-03-24': 30,
          'ADD-CRE500-ST-25-03-24': 40,
          'ADD-CRE1000-ST-25-03-24': 50,
        };

        // Sort modules by quantity
        modules.sort((a, b) => a.included_quantity - b.included_quantity);

        // Generate package cards with Black Friday styling
        let packagesHtml = '';
        modules.forEach((module) => {
          const totalCost =
            module.price_for_quantity * module.included_quantity;
          const totalPrice = totalCost.toFixed(2);
          const unitPrice = (totalCost / module.included_quantity).toFixed(2);

          // Check if this is a Black Friday module
          const isBlackFridayModule = module.sku.startsWith('ADD-CREBF');

          // Get discount percentage - for BF modules check hardcoded config, otherwise calculate
          let discountPercent = 0;
          let originalPrice = null;
          if (isBlackFridayModule) {
            // Find matching regular SKU discount
            const regularSku = Object.keys(bfDiscounts).find((sku) => {
              const qty = parseInt(sku.match(/ADD-CRE(\d+)/)?.[1] || '0');
              return qty === module.included_quantity;
            });
            discountPercent = regularSku ? bfDiscounts[regularSku] : 0;
            // Calculate original price before discount
            if (discountPercent > 0) {
              originalPrice = (totalCost / (1 - discountPercent / 100)).toFixed(
                2
              );
            }
          }

          packagesHtml += `
          <div class="col-md-6 col-lg-3 mb-3">
            <div class="bf-package-card ${
              isBlackFridayModule ? 'bf-highlighted' : ''
            }"
              data-module-id="${module.objectId}"
              data-module-name="${module.name}"
              data-module-quantity="${module.included_quantity}"
              data-module-price="${totalPrice}">
              ${
                isBlackFridayModule
                  ? '<div class="bf-promo-badge">BLACK FRIDAY</div>'
                  : ''
              }
              <div class="bf-package-credits mb-2">
                ${module.included_quantity}
                <span>${
                  module.included_quantity === 1 ? 'credito' : 'crediti'
                }</span>
              </div>
              <div class="bf-package-price mb-1">
                ${
                  originalPrice
                    ? `<span class="bf-original-price">${originalPrice}</span>`
                    : ''
                }
                ${totalPrice} <small style="color: rgba(255,255,255,0.6)">i.e.</small>
              </div>
              <div class="bf-package-unit-price mb-3">${unitPrice}/credito</div>
              ${
                discountPercent > 0
                  ? `<div class="bf-package-discount">-${discountPercent}%</div>`
                  : '<div style="height: 20px;"></div>'
              }
            </div>
          </div>
        `;
        });

        container.html(packagesHtml);

        // Handle package selection - make entire card clickable
        // Remove any existing handlers first
        $('.bf-package-card').off('click.bfPackageSelect');
        $('.bf-package-card').on('click.bfPackageSelect', async function (e) {
          e.preventDefault();
          const packageCard = $(this).closest('.bf-package-card');
          const moduleId = packageCard.data('module-id');
          const moduleName = packageCard.data('module-name');
          const moduleQuantity = packageCard.data('module-quantity');
          const modulePrice = packageCard.data('module-price');

          // Close this modal
          $('#aiBlackFridayModal').modal('hide');

          // Call the existing payment handler with pre-selected module
          handleBuyAiCredits({
            title: 'Conferma Acquisto Black Friday',
            html: `
            <div class="text-center">
              <h5>${moduleName}</h5>
              <p class="lead">${moduleQuantity} crediti per €${modulePrice}</p>
              <p class="text-muted">I crediti saranno disponibili immediatamente dopo il pagamento.</p>
            </div>
          `,
            inputValue: moduleId,
            showCancelButton: true,
            confirmButtonText: 'Procedi al pagamento',
            cancelButtonText: 'Annulla',
          });
        });
      } catch (error) {
        console.error('Error loading Black Friday packages:', error);
        container.html(`
        <div class="col-12 text-center">
          <p class="text-danger">Errore nel caricamento dei pacchetti. Riprova più tardi.</p>
        </div>
      `);
      }
    }
  );
};
